import type { FlowNode, FlowNodeType } from '../types';

/**
 * What each step type is, in the words of the person building a flow.
 *
 * One table for the three places a type is shown — the step outline, the type
 * picker, and the map's colour stripe — so "dynamic_list" is never a label anybody
 * reads. The keys are still the interpreter's; only the words are ours.
 */
export const NODE_LABEL: Record<FlowNodeType, string> = {
  message: 'Message',
  buttons: 'Buttons',
  list: 'Menu',
  products: 'Show products',
  capture: 'Ask a question',
  condition: 'Branch',
  action: 'Call a system',
  dynamic_list: 'Menu from a system',
  set: 'Set values',
  handoff: 'Hand to a person',
  end: 'End',
  template: 'Send a template',
  tag: 'Label the chat',
  wait: 'Wait',
  jump: 'Go to another bot',
};

export const NODE_EXPLAIN: Record<FlowNodeType, string> = {
  message: 'Says something and moves on.',
  buttons: 'Says something with up to three buttons under it.',
  list: 'Says something with a menu of up to ten options behind one button.',
  products: 'Says something and shows the catalogue, one product, or a list of products. A basket the customer sends goes to a person.',
  capture: 'Asks a question, checks the answer, and remembers it.',
  condition: 'Goes one way or another depending on a value collected earlier.',
  action: 'Calls one of your systems and remembers parts of the answer.',
  dynamic_list: 'Calls one of your systems and offers what came back as a menu.',
  set: 'Remembers values without asking anything.',
  handoff: 'Gives the conversation to a person. Your routing rules run here, or name someone.',
  end: 'Says goodbye and stops.',
  template: 'Sends one of your approved templates — the only message that still reaches someone 24 hours after they last wrote.',
  tag: 'Puts a label on the conversation, or takes one off. Says nothing to the customer.',
  wait: 'Pauses, then carries on by itself. If the customer writes first, it carries on straight away.',
  jump: 'Continues the conversation in another of your bots, keeping everything collected so far.',
};

/** A colour per type, so the shape of a flow is readable before any label is. */
export const NODE_TONE: Record<FlowNodeType, string> = {
  message: 'var(--color-brand)',
  buttons: '#7C5BD6',
  list: '#7C5BD6',
  products: '#0F766E',
  capture: '#B7791F',
  condition: '#0E7490',
  action: '#B45309',
  dynamic_list: '#B45309',
  set: 'var(--color-muted)',
  handoff: '#BE185D',
  end: 'var(--color-muted)',
  template: 'var(--color-brand)',
  tag: '#0E7490',
  wait: 'var(--color-muted)',
  jump: '#7C5BD6',
};

/** The order the type picker lists them in: the common ones first. */
export const NODE_ORDER: FlowNodeType[] = [
  'message', 'buttons', 'list', 'products', 'template',
  'capture', 'condition', 'action', 'dynamic_list',
  'set', 'tag', 'wait', 'jump', 'handoff', 'end',
];

/**
 * A new step of a type, with the fields the validator will ask for already present.
 *
 * A step created empty fails validation on the spot ("needs a 'body'") before anybody
 * has typed a letter into it, which reads as a scolding. Present-but-blank fields
 * fail the same rule, so this does not hide anything; it just puts the shape on
 * screen so the form has somewhere to type.
 */
export function blankNode(type: FlowNodeType): FlowNode {
  switch (type) {
    case 'message': return { type, body: '', next: null };
    case 'buttons': return { type, body: '', buttons: [], transitions: {} };
    case 'list': return { type, body: '', button: 'Menu', rows: [], transitions: {} };
    case 'products': return { type, body: '', mode: 'catalogue', products: [], next: null };
    case 'capture': return { type, prompt: '', var: '', validation: { type: 'nonempty' }, next: null };
    case 'condition': return { type, var: '', cases: {}, default: null };
    case 'action': return { type, action: '', mode: 'pull', response_map: {}, next: null };
    case 'dynamic_list': return { type, action: '', items_path: '', var: '', next: null, empty_next: null };
    case 'set': return { type, set: {}, next: null };
    case 'handoff': return { type, body: '' };
    case 'end': return { type, body: '' };
    case 'template': return { type, template: '', variables: [], next: null };
    case 'tag': return { type, add: [], remove: [], next: null };
    // Ten minutes: long enough to be a pause somebody meant, short enough to still be
    // inside the window where an ordinary message can reach them.
    case 'wait': return { type, minutes: 10, next: null };
    case 'jump': return { type, flow: '' };
  }
}

/**
 * Changing a step's type keeps what still makes sense and drops what does not.
 *
 * A message turned into a menu keeps its text and its "then go to" as the menu's
 * fallback target is not a thing, so `next` goes; a menu turned into a message keeps
 * the text and loses its options rather than carrying a `rows` array the validator
 * would then complain about on a message. The alternative — keeping every key and
 * letting the validator sort it out — is how a step ends up with `buttons` and
 * `cases` at once.
 */
export function retype(node: FlowNode, type: FlowNodeType): FlowNode {
  const fresh = blankNode(type);
  const keep: FlowNode = {};
  const text = (node.body ?? node.prompt) as string | undefined;

  if ('body' in fresh && typeof text === 'string') keep.body = text;
  if ('prompt' in fresh && typeof text === 'string') keep.prompt = text;
  if ('next' in fresh && typeof node.next === 'string') keep.next = node.next;
  if ('var' in fresh && typeof node.var === 'string') keep.var = node.var;
  if ('action' in fresh && typeof node.action === 'string') keep.action = node.action;
  if ('transitions' in fresh && node.transitions && typeof node.transitions === 'object') keep.transitions = node.transitions;
  if (type === 'buttons' && Array.isArray(node.rows)) keep.buttons = (node.rows as unknown[]).slice(0, 3);
  if (type === 'list' && Array.isArray(node.buttons)) keep.rows = node.buttons;
  if (node.set && typeof node.set === 'object') keep.set = node.set;
  for (const key of ['header', 'footer', 'invalid_msg'] as const) {
    if (typeof node[key] === 'string' && (type === 'list' || type === 'buttons' || type === 'dynamic_list' || type === 'products')) keep[key] = node[key];
  }

  return { ...fresh, ...keep, type };
}

/** The step's own text, for the outline: what a customer would read. */
export function nodeSummary(node: FlowNode): string {
  const text = (node.body ?? node.prompt) as unknown;
  if (typeof text === 'string' && text.trim()) return text.trim().replace(/\s+/g, ' ');
  if (node.type === 'condition') return `on ${String(node.var || '…')}`;
  if (node.type === 'action' || node.type === 'dynamic_list') return String(node.action || 'no action yet');
  if (node.type === 'set') return Object.keys((node.set as object) ?? {}).join(', ') || 'nothing yet';
  if (node.type === 'template') return String(node.template || 'no template yet');
  if (node.type === 'tag') {
    const add = (node.add as unknown[]) ?? [];
    const remove = (node.remove as unknown[]) ?? [];
    const parts = [...add.map((t) => `+${String(t)}`), ...remove.map((t) => `-${String(t)}`)];
    return parts.join(', ') || 'nothing yet';
  }
  if (node.type === 'wait') {
    const minutes = Number(node.minutes) || 0;
    if (minutes > 0) return minutes === 1 ? '1 minute' : `${minutes} minutes`;
    const seconds = Number(node.seconds) || 0;
    return seconds > 0 ? `${seconds} seconds` : 'no time set';
  }
  if (node.type === 'jump') return String(node.flow || 'no bot named yet');
  return '';
}
